import * as React from "react";
import { cn } from "../../lib/utils";

interface StackProps extends React.HTMLAttributes<HTMLDivElement> {
  direction?: "row" | "column";
  spacing?: 0 | 1 | 2 | 3 | 4 | 5 | 6 | 8 | 10 | 12 | 16 | 20 | 24 | 32;
  alignItems?: "start" | "center" | "end" | "stretch" | "baseline";
  justifyContent?: "start" | "center" | "end" | "between" | "around" | "evenly";
  variant?: "flex" | "grid";
  columns?: 1 | 2 | 3 | 4 | 5 | 6;
  divider?: boolean | React.ReactNode;
  children: React.ReactNode;
}

export const Stack = React.forwardRef<HTMLDivElement, StackProps>(
  (
    {
      direction = "column",
      spacing = 4,
      alignItems,
      justifyContent,
      variant = "flex",
      columns = 3,
      divider,
      className,
      children,
      ...props
    },
    ref,
  ) => {
    const spacingClasses = {
      0: "",
      1: "gap-1",
      2: "gap-2",
      3: "gap-3",
      4: "gap-4",
      5: "gap-5",
      6: "gap-6",
      8: "gap-8",
      10: "gap-10",
      12: "gap-12",
      16: "gap-16",
      20: "gap-20",
      24: "gap-24",
      32: "gap-32",
    };

    const alignItemsClasses = {
      start: "items-start",
      center: "items-center",
      end: "items-end",
      stretch: "items-stretch",
      baseline: "items-baseline",
    };

    const justifyContentClasses = {
      start: "justify-start",
      center: "justify-center",
      end: "justify-end",
      between: "justify-between",
      around: "justify-around",
      evenly: "justify-evenly",
    };

    const gridColumnClasses = {
      1: "xl:grid-cols-1",
      2: "xl:grid-cols-2",
      3: "lg:grid-cols-2 xl:grid-cols-3 ",
      4: "xl:grid-cols-4",
      5: "xl:grid-cols-5",
      6: "xl:grid-cols-6",
    };

    const renderChildren = () => {
      if (!divider || !children) return children;

      const childrenArray = React.Children.toArray(children);
      if (childrenArray.length <= 1) return children;

      const defaultDivider =
        direction === "row" ? (
          <div className="border-l border-border h-full" />
        ) : (
          <div className="border-t border-border w-full" />
        );

      const dividerElement =
        typeof divider === "boolean" ? defaultDivider : divider;

      return childrenArray.map((child, index) => (
        <React.Fragment key={index}>
          {child}
          {index < childrenArray.length - 1 && dividerElement}
        </React.Fragment>
      ));
    };

    return (
      <div
        ref={ref}
        className={cn(
          // Base classes
          variant === "grid" && direction === "row"
            ? "grid grid-cols-1" // Grid layout
            : "flex", // Flex layout (default)

          // Direction classes
          variant === "grid" && direction === "row"
            ? gridColumnClasses[columns] // Grid columns for desktop
            : direction === "column"
              ? "flex-col"
              : "grid grid-cols-1 md:flex md:flex-row",

          // Width for row direction
          direction === "row" && "w-full",

          // Alignment classes (only apply to flex, not grid)
          variant === "flex" && alignItems && alignItemsClasses[alignItems],
          variant === "flex" &&
            justifyContent &&
            justifyContentClasses[justifyContent],

          // Spacing
          spacingClasses[spacing],
          className,
        )}
        {...props}
      >
        {React.Children.map(renderChildren(), (child) => {
          if (React.isValidElement(child) && direction === "row") {
            const existingClassName = child.props.className || "";

            if (variant === "grid") {
              // For grid variant, don't modify child classes - let CSS Grid handle the layout
              return child;
            } else {
              // For flex variant, use responsive approach
              return React.cloneElement(child, {
                ...child.props,
                className: cn(
                  "w-full min-w-0 md:w-auto", // Full width on mobile with min-w-0 to prevent overflow, auto on desktop
                  existingClassName,
                ),
              });
            }
          }
          return child;
        })}
      </div>
    );
  },
);

Stack.displayName = "Stack";
